import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import MoreHorizOutlinedIcon from "@mui/icons-material/MoreHorizOutlined";
import {
  type ChangeEvent,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import FrontendLoggerFactory from "utils/logging/FrontendLoggerFactory";

import { adminUiCopy } from "@/copy/adminUiCopy";
import { useUploadAdminV2ProfilePhotoMutation } from "@/store/api/baseApi";
import { useShellDismiss } from "@/vnext/app-shell/useShellDismiss";
import { cx } from "@/vnext/shared/cx";

const logger =
  FrontendLoggerFactory.getLogger().withComponent("ShellAccountBlock");
const COPIED_MS = 1200;

export interface ShellAccountIdentity {
  label: string;
  email: string;
  userId: string;
  profilePhotoUrl: string | null | undefined;
}

const initialsOf = (label: string) =>
  label
    .split(/\s+/u)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("") || "A";

function Avatar({ identity }: { identity: ShellAccountIdentity }) {
  return (
    <span aria-hidden="true" className="vnext-shell-account__avatar">
      {identity.profilePhotoUrl ? (
        <img alt="" src={identity.profilePhotoUrl} />
      ) : (
        initialsOf(identity.label)
      )}
    </span>
  );
}

function AdminOnlyChip() {
  return (
    <span className="vnext-shell-account__chip">
      {adminUiCopy.shellChrome.adminOnly}
    </span>
  );
}

/**
 * The signed-in block (F-016, F-017, F-022) and its account popover
 * (F-208): who is signed in, their access and the environment, then Change
 * photo and Sign out. Screenshot mode passes a redacted identity.
 */
export function ShellAccountBlock({
  buildLabel,
  collapsed,
  environmentLine,
  identity,
  isLoggingOut,
  onSignOut,
}: {
  buildLabel: string;
  collapsed: boolean;
  environmentLine: string;
  identity: ShellAccountIdentity;
  isLoggingOut: boolean;
  onSignOut: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">(
    "idle",
  );
  const [photoError, setPhotoError] = useState<string | null>(null);
  const [uploadProfilePhoto, { isLoading: isUploading }] =
    useUploadAdminV2ProfilePhotoMutation();
  const popoverId = useId();
  const blockRef = useRef<HTMLButtonElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const close = useCallback((restoreFocus: boolean) => {
    setOpen(false);
    if (restoreFocus) blockRef.current?.focus();
  }, []);
  useShellDismiss({
    open,
    onClose: close,
    refs: useMemo(() => [blockRef, popoverRef], []),
  });

  useEffect(() => {
    if (copyState === "idle") return undefined;
    const timeout = window.setTimeout(() => setCopyState("idle"), COPIED_MS);
    return () => window.clearTimeout(timeout);
  }, [copyState]);

  const copyUserId = () => {
    // The clipboard is missing outside secure contexts: say so briefly.
    try {
      void navigator.clipboard
        .writeText(identity.userId)
        .then(() => setCopyState("copied"))
        .catch(() => setCopyState("failed"));
    } catch {
      setCopyState("failed");
    }
  };

  const onPhotoChosen = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0] ?? null;
    event.currentTarget.value = "";
    if (!file) return;
    setPhotoError(null);
    try {
      await uploadProfilePhoto({ file }).unwrap();
    } catch (error) {
      setPhotoError("Use a PNG or JPG under 1 MB.");
      logger.warn("Admin profile photo upload failed in shell chrome", {
        operation: "admin_shell_profile_photo_upload_failed",
        errorName: error instanceof Error ? error.name : "UnknownError",
      });
    }
  };

  // F-208 names the action "Change photo", with or without a photo yet.
  const photoLabel = isUploading ? "Uploading..." : "Change photo";

  return (
    <div className={cx("vnext-shell-account", collapsed && "is-collapsed")}>
      <input
        accept="image/png,image/jpeg"
        className="vnext-shell-account__file"
        disabled={isUploading}
        onChange={(event) => void onPhotoChosen(event)}
        ref={fileRef}
        type="file"
      />
      {/* WCAG 2.5.3 Label in Name: the visible lines name the block, and a
          hidden "Open user menu" follows them. The spaces keep the lines
          separate words; flex layout does not render them. Collapsed, the
          lines are not displayed, so a hidden copy of the name stands in. */}
      <button
        aria-controls={open ? popoverId : undefined}
        aria-expanded={open}
        className="vnext-shell-account__block"
        // The name tooltip must not sit over the open account popover.
        data-tooltip={collapsed && !open ? identity.label : undefined}
        onClick={() => setOpen((current) => !current)}
        ref={blockRef}
        type="button"
      >
        <Avatar identity={identity} />
        <span className="vnext-shell-account__lines">
          <span className="vnext-shell-account__name">{identity.label}</span>{" "}
          <AdminOnlyChip />{" "}
          <span className="vnext-shell-account__environment">
            {environmentLine}
          </span>
        </span>
        {collapsed ? (
          <span className="vnext-sr-only">{identity.label}</span>
        ) : null}{" "}
        <span className="vnext-sr-only">
          {adminUiCopy.shellChrome.openUserMenu}
        </span>
        <MoreHorizOutlinedIcon
          aria-hidden="true"
          className="vnext-shell-account__more"
          sx={{ fontSize: 16 }}
        />
      </button>
      {open ? (
        <div
          aria-label={adminUiCopy.routeGateSummary.signedInAccount}
          className="vnext-shell-account__popover"
          id={popoverId}
          ref={popoverRef}
          role="group"
        >
          <div className="vnext-shell-account__eyebrow">
            {adminUiCopy.shellChrome.signedInAs}
          </div>
          <div className="vnext-shell-account__popover-name">
            {identity.label}
          </div>
          <div className="vnext-shell-account__email">{identity.email}</div>
          <div className="vnext-shell-account__user-id">
            <span>
              {adminUiCopy.shellChrome.userIdLabel}: {identity.userId}
            </span>
            <button
              className="vnext-shell-account__copy"
              onClick={copyUserId}
              type="button"
            >
              {copyState === "copied"
                ? "Copied"
                : copyState === "failed"
                  ? "Couldn't copy"
                  : "Copy"}
            </button>
          </div>
          <div className="vnext-shell-account__access">
            <AdminOnlyChip />
            {/* F-154–F-160: no browser tooltips, so the build that the
                block's old `title` named is read here instead. */}
            <span>{`${environmentLine} · ${buildLabel}`}</span>
          </div>
          <button
            aria-label={
              isUploading ? "Uploading profile photo" : "Change profile photo"
            }
            className="vnext-shell-account__action"
            disabled={isUploading}
            onClick={() => {
              setPhotoError(null);
              fileRef.current?.click();
            }}
            type="button"
          >
            <CameraAltOutlinedIcon aria-hidden="true" sx={{ fontSize: 15 }} />
            {photoLabel}
          </button>
          {photoError ? (
            <div className="vnext-shell-account__error" role="status">
              {photoError}
            </div>
          ) : null}
          <button
            className="vnext-shell-account__action"
            disabled={isLoggingOut}
            onClick={() => {
              setOpen(false);
              onSignOut();
            }}
            type="button"
          >
            <LogoutOutlinedIcon aria-hidden="true" sx={{ fontSize: 15 }} />
            {adminUiCopy.common.signOut}
          </button>
        </div>
      ) : null}
    </div>
  );
}
